import { useEffect, useRef } from 'react';
import styled, { css } from 'styled-components';
import { Show } from '@elfsight/lib-utils';
import { useFormDispatch } from '@elfsight/widget-form-builder/widget';
import { PaymentInfoBlock } from '@elfsight/widget-stripe-payment';
import { Icon, Typography } from '@elfsight/widget-ui';
import {
  createGCEventURL,
  createReadableClass,
  openWindow
} from '@elfsight/widget-utils';
import { useLingui } from '@lingui/react/macro';
import { useSettings } from '@wu/widget-core';
import { ActionButton } from '../../components/action-button';
import { Body1 } from '../../components/typography';
import { useCurrentTimeZone } from '../../current-timezone-provider';
import { CurrentTimeslot } from '../../datetime/current-timeslot';
import { useDatetime } from '../../datetime/datetime-provider';
import { useEventContext } from '../../event-provider';
import { usePaymentData } from '../../payment/payment-data-provider';
import { ServiceCard } from '../../service/service-card/service-card';
import { useServices } from '../../service/services-provider';
import { useWidgetDimensions } from '../../widget-dimensions-provider';
import SuccessIcon from './success-icon.svg?react';

const successColor = '#0E8B25';

export function SuccessMessage() {
  const { t } = useLingui();
  const settings = useSettings();
  const { currentService } = useServices();
  const { publish } = useEventContext();
  const dispatch = useFormDispatch();
  const { selectedTimeslot } = useDatetime();
  const { timeZone } = useCurrentTimeZone();
  const { isMobile } = useWidgetDimensions();
  const { processedPaymentInfo } = usePaymentData();

  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollIntoView({
        block: 'start',
        behavior: 'smooth'
      });
    }
  }, []);

  const handleAddToCalendar = () => {
    if (!currentService || !selectedTimeslot) {
      return;
    }

    return openWindow(
      createGCEventURL({
        text: currentService.name,
        details: currentService.description,
        location: settings.businessContactsAddress,
        dates: [selectedTimeslot?.from, selectedTimeslot.to],
        timeZone
      }),
      { target: '_blank' }
    );
  };

  const readyToWatchRef = useRef(false);
  useEffect(() => {
    const sleep = setTimeout(() => {
      readyToWatchRef.current = true;
    }, 1000);

    return () => {
      clearTimeout(sleep);
    };
  }, []);

  useEffect(() => {
    if (readyToWatchRef.current && settings.editMode) {
      publish('resetAll');
      dispatch({ type: 'reset' });
    }
  }, [settings]);

  return (
    <Container ref={containerRef}>
      <Jumbotron>
        <IconContainer>
          <Icon fill={successColor} size={48} component={SuccessIcon} />
        </IconContainer>
        <TextContainer>
          <Typography
            color={successColor}
            textStyle={{ fontSize: isMobile ? 22 : 24, fontWeight: 700 }}
          >
            {t`Booking Confirmed`}
          </Typography>
          <Description _isMobile={isMobile}>
            {t`We are pleased to inform you that your reservation request has been received and confirmed. Thank you!`}
          </Description>
        </TextContainer>
      </Jumbotron>

      <div>
        <ServiceCard service={currentService} />
        <CurrentTimeslot canChange={false} />
        <Show when={!!processedPaymentInfo}>
          <PaymentInfoBlock
            color={settings.textColor}
            accentColor={settings.accentColor}
            dividers={{ bottom: true }}
            {...processedPaymentInfo!}
          />
        </Show>
      </div>

      <ActionButton onClick={handleAddToCalendar}>
        {t`Add to Google Calendar`}
      </ActionButton>
    </Container>
  );
}

const Container = styled.div.attrs({
  className: createReadableClass({
    component: 'success-message',
    element: 'container'
  })
})`
  display: flex;
  flex-direction: column;
  gap: 40px;
  width: 100%;
`;

const Jumbotron = styled.div.attrs({
  className: createReadableClass({
    component: 'success-message',
    element: 'jumbotron'
  })
})`
  display: flex;
  flex-direction: column;
  margin: 0 auto;
  align-items: center;
  max-width: 480px;
`;

const IconContainer = styled.div.attrs({
  className: createReadableClass({
    component: 'success-message',
    element: 'icon-container'
  })
})`
  margin-bottom: 16px;
`;

const TextContainer = styled.div.attrs({
  className: createReadableClass({
    component: 'success-message',
    element: 'text-container'
  })
})`
  text-align: center;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Description = styled(Body1).attrs({
  className: createReadableClass({
    component: 'success-message',
    element: 'description'
  })
})<{ _isMobile: boolean }>`
  ${({ _isMobile }) =>
    _isMobile &&
    css`
      font-size: 14px;
    `}
`;
